import {
  Button,
  Flex,
  TabList,
  Tabs,
  useMultiStyleConfig,
  useTab,
} from '@chakra-ui/react';
import React from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router';
import { Link, useMatch } from 'react-router-dom';

import {
  PermissionGuard,
  PermissionsFn,
  usePermissionGuard,
} from '~/core/PermissionGuard';
import {
  Route as ReportBuilderRoute,
  permissions as reportBuilderPermissions,
} from '~/core/routes/analytics/report-builder/_index';
import {
  Route as RiskRoute,
  permissions as riskPermissions,
} from '~/core/routes/analytics/risk/_index';
import {
  Route as StratsRoute,
  permissions as stratsPermissions,
} from '~/core/routes/analytics/strats/_index';
import { Typography } from '~/packages/common-frontend/components/Typography';

// Fragments
type NavTabProps = { children: React.ReactNode; end?: boolean; to: string };
const NavTab = ({ children, to, end = false }: NavTabProps) => {
  const match = useMatch({ path: to, end });
  const tabProps = useTab({});
  const styles = useMultiStyleConfig('Tabs', tabProps);
  return (
    <Button
      __css={styles.tab}
      _active={{ fontWeight: 'bold', borderBottomColor: 'black' }}
      borderBottom="2px solid transparent"
      as={Link}
      isActive={!!match}
      alignItems="center"
      display="inline-flex"
      justifyContent="center"
      margin={0}
      textAlign="center"
      to={to}
    >
      {children}
    </Button>
  );
};

const NavigateToFirstAvailableTab = () => {
  const riskTabEnabled = usePermissionGuard(riskPermissions) === 'allow';
  const stratsTabEnabled = usePermissionGuard(stratsPermissions) === 'allow';
  const reportBuilderTabEnabled =
    usePermissionGuard(reportBuilderPermissions) === 'allow';

  if (riskTabEnabled) return <Navigate to="./risk" replace />;
  if (stratsTabEnabled) return <Navigate to="./strats" replace />;
  if (reportBuilderTabEnabled) {
    return <Navigate to="./report-builder" replace />;
  }

  return <Navigate to="/" replace />;
};

const AnalyticsTemplate = ({ children }: { children: React.ReactNode }) => (
  <Flex
    direction="column"
    padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
    gap="1.5rem"
  >
    <Typography variant="page-title" trim>
      Analytics
    </Typography>
    <Tabs variant="line" colorScheme="black">
      <TabList overflowX="auto" gap="2rem" style={{ scrollbarWidth: 'thin' }}>
        <PermissionGuard permissions={riskPermissions}>
          <NavTab to="/analytics/risk">Risk</NavTab>
        </PermissionGuard>
        <PermissionGuard permissions={stratsPermissions}>
          <NavTab to="/analytics/strats">Strats</NavTab>
        </PermissionGuard>
        <PermissionGuard permissions={reportBuilderPermissions}>
          <NavTab to="/analytics/report-builder">Report Builder</NavTab>
        </PermissionGuard>
      </TabList>
    </Tabs>
    <Flex direction="column">{children}</Flex>
  </Flex>
);

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';
  if (context.user.userType === 'BORROWER') return 'deny';
  return context.flags.productsAnalyticsPermissions;
};
export const Router: RouteObject = {
  element: (
    <PermissionGuard
      permissions={permissions}
      fallback={<Navigate to="/" replace />}
    >
      <AnalyticsTemplate>
        <Outlet />
      </AnalyticsTemplate>
    </PermissionGuard>
  ),
  // Relative to /analytics/*
  children: [
    { path: 'risk', element: <RiskRoute /> },
    { path: 'strats', element: <StratsRoute /> },
    { path: 'report-builder', element: <ReportBuilderRoute /> },
    { path: '*', element: <NavigateToFirstAvailableTab /> },
  ],
};
